(() => {
  'use strict';

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const headerGroup = document.querySelector('#header-group');
  const pageWrapper = document.querySelector('.page-wrapper');
  const main = document.querySelector('#MainContent');

  /* Premium UI behaviour is global; editorial scroll choreography remains homepage-specific. */
  root.classList.add('mv-premium-ui');

  const updateHeader = () => {
    const y = Math.max(window.scrollY || 0, pageWrapper?.scrollTop || 0);
    headerGroup?.classList.toggle('mv-header-scrolled', y > 18);
  };

  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
  pageWrapper?.addEventListener('scroll', updateHeader, { passive: true });

  /* ---------- HAMBURGER DRAWER ---------- */
  const prepareMenuDrawer = (scope = document) => {
    const containers = [];

    if (scope instanceof Element && scope.matches('.menu-drawer-container')) {
      containers.push(scope);
    }

    scope.querySelectorAll?.('.menu-drawer-container').forEach((container) => containers.push(container));

    containers.forEach((container) => {
      if (container.dataset.mvPremiumPrepared === 'true') return;
      container.dataset.mvPremiumPrepared = 'true';

      const animated = container.querySelectorAll('.menu-drawer__animated-element');
      animated.forEach((element, index) => {
        if (!element.style.getPropertyValue('--menu-drawer-animation-index')) {
          element.style.setProperty('--menu-drawer-animation-index', String(index + 1));
        }
      });

      container.addEventListener('toggle', () => {
        root.classList.toggle('mv-menu-open', container.open);
      });
    });
  };

  prepareMenuDrawer();

  /* ---------- CART + THEME DRAWERS ---------- */
  const prepareThemeDrawer = (drawer) => {
    if (!(drawer instanceof Element) || !drawer.matches('theme-drawer')) return;

    const header = drawer.querySelector('.theme-drawer__header');
    const title = drawer.querySelector('.theme-drawer__title');
    const summary = drawer.querySelector('.cart-drawer__summary');
    const content = drawer.querySelector('.cart-drawer__content');

    if (header) {
      header.classList.add('mv-premium-drawer-rise');
      header.style.setProperty('--mv-drawer-delay', '90ms');
    }

    if (title && !header?.contains(title)) {
      title.classList.add('mv-premium-drawer-rise');
      title.style.setProperty('--mv-drawer-delay', '110ms');
    }

    if (content) {
      content.classList.add('mv-premium-drawer-fade');
      content.style.setProperty('--mv-drawer-delay', '180ms');
    }

    if (summary) {
      summary.classList.remove('mv-premium-drawer-fade');
      summary.classList.add('mv-premium-drawer-rise');
      summary.style.setProperty('--mv-drawer-delay', '260ms');
    }
  };

  const prepareAllThemeDrawers = (scope = document) => {
    if (scope instanceof Element && scope.matches('theme-drawer')) {
      prepareThemeDrawer(scope);
    }
    scope.querySelectorAll?.('theme-drawer').forEach(prepareThemeDrawer);
  };

  const replayDrawerMotion = (drawer) => {
    if (!(drawer instanceof Element)) return;
    prepareThemeDrawer(drawer);

    drawer.classList.remove('mv-premium-drawer-open');
    void drawer.offsetWidth;
    requestAnimationFrame(() => drawer.classList.add('mv-premium-drawer-open'));
  };

  prepareAllThemeDrawers();

  document.addEventListener('theme-drawer:open', (event) => {
    const target = event.target instanceof Element ? event.target.closest('theme-drawer') : null;
    if (target) replayDrawerMotion(target);
  });

  document.addEventListener('theme-drawer:close', (event) => {
    const target = event.target instanceof Element ? event.target.closest('theme-drawer') : null;
    target?.classList.remove('mv-premium-drawer-open');
  });

  if (!reducedMotion) {
    root.classList.add('mv-premium-ui-ready');
  }

  /* ---------- HOMEPAGE EDITORIAL REVEALS ---------- */
  const hero = main?.querySelector("[id*='hero_MV01']");
  let enhanceHomepage = () => {};

  if (main && hero) {
    root.classList.add('mv-premium-motion');

    if (!reducedMotion) {
      const bound = new WeakSet();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const element = entry.target;
          element.classList.add('mv-is-visible');
          observer.unobserve(element);

          window.setTimeout(() => {
            element.classList.add('mv-premium-complete');
          }, 1750);
        });
      }, {
        root: null,
        threshold: 0.12,
        rootMargin: '0px 0px -12% 0px'
      });

      const bindReveal = (element, delay = 0) => {
        if (!(element instanceof Element) || bound.has(element)) return;

        bound.add(element);
        element.classList.add('mv-premium-reveal');
        element.style.setProperty('--mv-premium-delay', `${delay}ms`);
        observer.observe(element);
      };

      const bindChildren = (container, step = 150, start = 0) => {
        if (!container) return false;
        const children = [...container.children].filter((child) => child instanceof Element);
        if (!children.length) return false;

        children.forEach((child, index) => bindReveal(child, start + index * step));
        return true;
      };

      enhanceHomepage = () => {
        const pulse = main.querySelector("[id*='pulse_shop_MV01']");
        if (pulse) {
          bindReveal(pulse.querySelector('.mv-pulse-shop__header'), 0);
          pulse.querySelectorAll('.mv-pulse-card').forEach((card, index) => {
            bindReveal(card, 150 + index * 210);
          });
        }

        ['editorial_MV01', 'signature_MV01', 'collective_MV01'].forEach((key) => {
          const section = main.querySelector(`[id*='${key}']`);
          if (!section) return;

          const resourceList = section.querySelector('.resource-list');
          if (bindChildren(resourceList, 170, 0)) return;

          const contentWrapper = section.querySelector('.section-content-wrapper, .section-resource-list, .section');
          bindReveal(contentWrapper || section.firstElementChild, 0);
        });
      };

      enhanceHomepage();
      root.classList.add('mv-premium-motion-ready');
    }
  }

  /* Shopify editor + dynamically re-rendered cart/menu content. */
  let refreshQueued = false;
  const refreshDynamicUi = (scope = document) => {
    if (refreshQueued) return;
    refreshQueued = true;

    requestAnimationFrame(() => {
      refreshQueued = false;
      prepareMenuDrawer(scope);
      prepareAllThemeDrawers(scope);
      enhanceHomepage();
      updateHeader();
    });
  };

  document.addEventListener('shopify:section:load', (event) => {
    refreshDynamicUi(event.target instanceof Element ? event.target : document);
  });

  const dynamicContent = new MutationObserver((mutations) => {
    const added = mutations.flatMap((mutation) => [...mutation.addedNodes]);
    const element = added.find((node) => node instanceof Element);
    if (element) refreshDynamicUi(element);
  });

  dynamicContent.observe(document.body, { childList: true, subtree: true });
})();
